import { Loader, RefreshCcw, Calculator, CheckCircle2, AlertCircle } from 'lucide-react'

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/modules/shared/components/ui/table'
import { cn } from '@/modules/shared/lib/utils'
import { formatter } from '@/modules/shared/utils/number-format'

import React from 'react'
import { useOrderFormHeaderComponent } from './header-component.hook'

export function HeaderComponent() {
	const {
		paramsLayoutOrderForm,
		isCalculatingOrder,
		isCalculationOrder,
		jsonOrderForm,
		autoCalculateEnabled,
		onCalculationOrder,
	} = useOrderFormHeaderComponent()

	const headerFields = [
		{ field: 'field1', fallback: 'ITENS', dataKey: 'qtdItem' },
		{ field: 'field2', fallback: 'M³', dataKey: 'cubagem', isValue: true },
		{ field: 'field3', fallback: 'PESO', dataKey: 'pesoBruto', isValue: true },
		{ field: 'field4', fallback: 'TOTAL PRODUTOS', dataKey: 'valorProdutos', isValue: true },
	]

	const renderTableHeader = (field: string, fallback: string) => {
		const fieldConfig = paramsLayoutOrderForm?.geral?.find(
			(item) => item.campo === field,
		)

		return (
			<TableHead className="text-center h-12 bg-slate-100 border-slate-200 text-slate-700 font-semibold text-sm min-h-[48px] max-h-[48px]">
				<div className="flex items-center justify-center h-full">
					{fieldConfig?.label ?? fallback}
				</div>
			</TableHead>
		)
	}

	const renderTableCell = (field: string, dataKey: string, isValue = false) => {
		const fieldConfig = paramsLayoutOrderForm?.geral?.find(
			(item) => item.campo === field,
		)

		if (!jsonOrderForm) return null

		if (!fieldConfig?.fieldName) {
			return (
				<TableCell className="p-2 h-10 border-none border-slate-100">
					<div className="flex justify-center items-center">
						{isValue ? formatter(2, jsonOrderForm[dataKey]) : jsonOrderForm[dataKey]}
					</div>
				</TableCell>
			)
		}

		if (fieldConfig.tipoCampo === 'decimal') {
			return (
				<TableCell className="p-2 h-10 border-slate-100">
					<div className="flex justify-center items-center font-medium">
						{formatter(fieldConfig.tamanhoCampo, jsonOrderForm[fieldConfig.fieldName])}
					</div>
				</TableCell>
			)
		}

		return (
			<TableCell className="p-2 h-10 border-slate-100">
				<div className="flex justify-center items-center">
					{fieldConfig.fieldName === 'statusLl' ? (
						renderStatusIndicator()
					) : (
						<span className={cn(getStatusTextColor(), 'font-semibold')}>
							{formatter(2, jsonOrderForm[fieldConfig.fieldName])}
						</span>
					)}
				</div>
			</TableCell>
		)
	}

	const getStatusTextColor = () => {
		const statusColors = {
			'4': 'text-amber-600',
			'6': 'text-red-600',
			'12': 'text-emerald-600',
		}
		return statusColors[jsonOrderForm?.statusLl] || 'text-slate-600'
	}

	const renderStatusIndicator = () => {
		const statusIndicators = {
			4: (
				<div className="flex items-center gap-2">
					<div className="bg-amber-500 rounded-full h-3 w-3 shadow-xs" />
				</div>
			),
			6: (
				<div className="flex items-center gap-2">
					<div className="bg-red-500 rounded-full h-3 w-3 shadow-xs" />
				</div>
			),
			12: (
				<div className="flex items-center gap-2">
					<div className="bg-emerald-500 rounded-full h-3 w-3 shadow-xs" />
				</div>
			),
		}

		return statusIndicators[jsonOrderForm?.statusLl] || null
	}

	const renderCalculationState = () => {
		const isCalculating = isCalculatingOrder || autoCalculateEnabled

		return (
			<TableCell colSpan={4} className="p-2 h-10 border-none border-slate-100">
				<div
					className={cn(
						"flex w-full justify-center items-center rounded-lg  transition-all duration-300",
						isCalculating
							? "   cursor-not-allowed"
							: "cursor-pointer hover:bg-slate-50   border-slate-300 hover:border-slate-400"
					)}
					onClick={!isCalculating ? onCalculationOrder : undefined}
					onKeyUp={(e) => {
						if (!isCalculating) {
							e.stopPropagation()
							onCalculationOrder()
						}
					}}
					role="button"
					tabIndex={isCalculating ? -1 : 0}
				>
					<div className="flex items-center gap-3">
						{isCalculating ? (
							<>
								<div className="relative">
									<Loader
										size={20}
										className="text-primary animate-spin"
									/>
									<div className="absolute inset-0 animate-ping">
										<div className="w-5 h-5 rounded-full opacity-20" />
									</div>
								</div>
								<div className="flex flex-col items-start">
									<span className="font-semibold text-slate-700 text-sm">
										Calculando...
									</span>
								</div>
							</>
						) : (
							<>
								<div className="relative group">
									<Calculator
										size={20}
										className="text-slate-600 group-hover:text-slate-800 transition-colors duration-200"
									/>
									<div className="absolute -inset-1 bg-slate-200 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-200" />
								</div>
								<div className="flex flex-col items-start">
									<span className="font-semibold text-slate-700 text-sm">
										Clique para calcular
									</span>

								</div>
							</>
						)}
					</div>
				</div>
			</TableCell>
		)
	}

	const renderLoadingOverlay = () => {
		if (!isCalculatingOrder) return null

		return (
			<div className="absolute inset-0 bg-white/80 backdrop-blur-xs flex items-center justify-center z-10 rounded-lg">
				<div className="flex items-center gap-3 bg-transparent rounded-lg p-4 ">
					<div className="relative">
						<Loader size={24} className="text-primary animate-spin" />
						<div className="absolute inset-0 animate-ping">
							<div className="w-6 h-6  rounded-full opacity-20" />
						</div>
					</div>
					<div className="flex flex-col">
						<span className="font-semibold text-slate-700 text-sm">
							Calculando...
						</span>
						<span className="text-slate-500 text-xs">
							Aguarde um momento
						</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="relative w-full bg-white rounded-lg h-22 max-h-22 shadow-xs border border-slate-200 overflow-hidden">
			{renderLoadingOverlay()}

			<Table>
				<TableHeader>
					<TableRow className="border-b border-slate-200">
						{headerFields.map(({ field, fallback }) => (
							<React.Fragment key={field}>
								{renderTableHeader(field, fallback)}
							</React.Fragment>
						))}
					</TableRow>
				</TableHeader>

				<TableBody>
					<TableRow className={cn(
						"transition-colors duration-150",
						isCalculatingOrder || autoCalculateEnabled
							? ""
							: "hover:bg-slate-50/50"
					)}>
						{isCalculationOrder ? (
							renderCalculationState()
						) : (
							<>
								{headerFields.map(({ field, dataKey, isValue }) => (
									<React.Fragment key={field}>
										{renderTableCell(field, dataKey, isValue)}
									</React.Fragment>
								))}
							</>
						)}
					</TableRow>
				</TableBody>
			</Table>
		</div>
	)
}